import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import {
  addWishlist,
  removeWishlist,
  getWishlist,
} from "../service/wishlist.service";

// =====================================================
// GET CUSTOMER ID
// =====================================================
const getCurrentCustomerId = () => {
  try {
    const selectedCustomer = JSON.parse(
      localStorage.getItem("selectedCustomer") || "null"
    );

    const id =
      selectedCustomer?.id ||
      selectedCustomer?.customer_id ||
      localStorage.getItem("customer_id");

    return id ? Number(id) : null;
  } catch (error) {
    console.error("Customer ID Error:", error);

    const id = localStorage.getItem("customer_id");

    return id ? Number(id) : null;
  }
};

// =====================================================
// GET USER ID
// =====================================================
const getCurrentUserId = () => {
  const id = localStorage.getItem("user_id");

  return id ? Number(id) : 1;
};

// =====================================================
// GET WISHLIST LIST FROM API RESPONSE
// =====================================================
const getWishlistList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data?.wishlist)) {
    return response.data.wishlist;
  }

  if (Array.isArray(response?.wishlist)) {
    return response.wishlist;
  }

  if (Array.isArray(response?.data?.data?.wishlist)) {
    return response.data.data.wishlist;
  }

  return [];
};

// =====================================================
// FETCH WISHLIST
// =====================================================
export const fetchWishlist = createAsyncThunk(
  "wishlist/fetch",

  async (
    { userId, customerId } = {},
    { rejectWithValue }
  ) => {
    try {
      const finalUserId =
        Number(userId) || getCurrentUserId();

      const finalCustomerId =
        Number(customerId) ||
        getCurrentCustomerId();

      if (!finalCustomerId) {
        return rejectWithValue({
          message: "Customer ID not found",
        });
      }

      const response = await getWishlist(
        finalUserId,
        finalCustomerId
      );

      return response;
    } catch (error) {
      console.error(
        "FETCH WISHLIST ERROR:",
        error
      );

      return rejectWithValue(
        error?.response?.data || {
          message: "Failed to fetch wishlist",
        }
      );
    }
  }
);

// =====================================================
// ADD WISHLIST
// =====================================================
export const addToWishlist = createAsyncThunk(
  "wishlist/add",

  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const userId =
        Number(payload?.user_id) ||
        getCurrentUserId();

      const customerId =
        Number(payload?.customer_id) ||
        getCurrentCustomerId();

      const productListingId = Number(
        payload?.product_listing_id
      );

      // ---------------------------------------------
      // CUSTOMER VALIDATION
      // ---------------------------------------------
      if (!customerId) {
        toast.error(
          "Please select customer first"
        );

        return rejectWithValue({
          message: "Customer ID is required",
        });
      }

      // ---------------------------------------------
      // PRODUCT VALIDATION
      // ---------------------------------------------
      if (!productListingId) {
        toast.error(
          "Product listing ID is required"
        );

        return rejectWithValue({
          message:
            "Product listing ID is required",
        });
      }

      // ---------------------------------------------
      // FINAL PAYLOAD
      // ---------------------------------------------
      const finalPayload = {
        user_id: userId,
        customer_id: customerId,
        product_listing_id: productListingId,
      };

      // ---------------------------------------------
      // ADD API
      // ---------------------------------------------
      const response =
        await addWishlist(finalPayload);

      // ---------------------------------------------
      // FRESH API DATA
      // ---------------------------------------------
      if (response?.success) {
        await dispatch(
          fetchWishlist({
            userId,
            customerId,
          })
        ).unwrap();
      }

      return response;
    } catch (error) {
      console.error(
        "ADD WISHLIST ERROR:",
        error
      );

      return rejectWithValue(
        error?.response?.data || {
          message: "Failed to add wishlist",
        }
      );
    }
  }
);

// =====================================================
// DELETE WISHLIST
// =====================================================
export const deleteWishlist = createAsyncThunk(
  "wishlist/delete",

  async (
    {
      wishlistId,
      userId,
      customerId,
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      if (!wishlistId) {
        return rejectWithValue({
          message: "Wishlist ID missing",
        });
      }

      const finalUserId =
        Number(userId) ||
        getCurrentUserId();

      const finalCustomerId =
        Number(customerId) ||
        getCurrentCustomerId();

      if (!finalCustomerId) {
        return rejectWithValue({
          message: "Customer ID missing",
        });
      }

      const response =
        await removeWishlist(
          wishlistId
        );

      if (response?.success) {
        toast.success(
          response?.message ||
            "Wishlist removed successfully"
        );

        // -------------------------------------------
        // FRESH API DATA
        // -------------------------------------------
        await dispatch(
          fetchWishlist({
            userId: finalUserId,
            customerId: finalCustomerId,
          })
        ).unwrap();
      } else {
        toast.error(
          response?.message ||
            "Unable to remove wishlist"
        );
      }

      return response;
    } catch (error) {
      console.error(
        "DELETE WISHLIST ERROR:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          "Delete failed"
      );

      return rejectWithValue(
        error?.response?.data || {
          message: "Delete failed",
        }
      );
    }
  }
);

// =====================================================
// SLICE
// =====================================================
const wishlistSlice = createSlice({
  name: "wishlist",

  initialState: {
    wishlist: [],
    wishlistCount: 0,
    loading: false,
    success: false,
    error: null,
  },

  reducers: {
    clearWishlist: (state) => {
      state.wishlist = [];
      state.wishlistCount = 0;
      state.success = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // =================================================
      // FETCH
      // =================================================
      .addCase(
        fetchWishlist.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchWishlist.fulfilled,
        (state, action) => {
          state.loading = false;
          state.error = null;

          const list =
            getWishlistList(
              action.payload
            );

          state.wishlist = list;

          // API DATA ONLY
          state.wishlistCount =
            list.length;

        }
      )

      .addCase(
        fetchWishlist.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload || null;

          // API fail par old data clear nahi karna
        }
      )

      // =================================================
      // ADD
      // =================================================
      .addCase(
        addToWishlist.pending,
        (state) => {
          state.loading = true;
          state.success = false;
        }
      )

      .addCase(
        addToWishlist.fulfilled,
        (state) => {
          state.loading = false;
          state.success = true;
        }
      )

      .addCase(
        addToWishlist.rejected,
        (state) => {
          state.loading = false;
          state.success = false;
        }
      )

      // =================================================
      // DELETE
      // =================================================
      .addCase(
        deleteWishlist.pending,
        (state) => {
          state.loading = true;
        }
      )

      .addCase(
        deleteWishlist.fulfilled,
        (state) => {
          state.loading = false;
          state.success = true;
        }
      )

      .addCase(
        deleteWishlist.rejected,
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const {
  clearWishlist,
} = wishlistSlice.actions;
export default wishlistSlice.reducer;